/**
 * 个人主页顶部用户信息卡片
 */

import React from 'react';
import {
  Paper,
  Box,
  Avatar,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Email as EmailIcon,
  DateRange as DateRangeIcon,
  Star as StarIcon,
} from '@mui/icons-material';

const ProfileHeader = ({ user, membershipInfo, growthInfo, formatDate, watchStats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!user) return null;

  const userProfile = user.profile || {};
  const userMembership = user.membership || {};
  const userPoints = user.points || {};
  
  // 优先使用watchStats中的数据，其次使用user.stats
  // API返回: total_dramas (观看剧集数), total_episodes (观看集数), total_watch_time (总观看时长)
  const overview = watchStats?.overview || {};
  const totalWatched = overview.total_dramas || overview.total_dramas_watched || 0;

  return (
    <Paper
      sx={{
        p: isMobile ? 2 : 4,
        borderRadius: 3,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* 装饰性背景图案 */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 120,
          height: 120,
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? 2 : 4,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* 头像区域 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar
            src={userProfile.avatar}
            sx={{
              width: isMobile ? 80 : 120,
              height: isMobile ? 80 : 120,
              border: '4px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              fontSize: isMobile ? 40 : 60,
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 'inherit' }} />
          </Avatar>

          {/* 会员徽章 */}
          <Chip
            icon={<span style={{ fontSize: 14 }}>{membershipInfo.icon}</span>}
            label={membershipInfo.nameCN}
            size="small"
            sx={{
              mt: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              color: 'white',
              fontWeight: 600,
              '& .MuiChip-icon': {
                color: 'white',
              },
            }}
          />
        </Box>

        {/* 用户信息区域 */}
        <Box
          sx={{
            flexGrow: 1,
            width: '100%',
            textAlign: isMobile ? 'center' : 'left',
          }}
        >
          {/* 用户名 */}
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: isMobile ? '1.5rem' : '2.25rem',
            }}
          >
            {userProfile.nickname || user.username}
          </Typography>

          {/* 邮箱 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              gap: 1,
              mb: 1.5,
              opacity: 0.9,
            }}
          >
            <EmailIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2">{user.email}</Typography>
            {user.email_verified && (
              <Chip
                label="已验证"
                size="small"
                sx={{
                  backgroundColor: 'rgba(16, 185, 129, 0.8)',
                  color: 'white',
                  height: 20,
                  fontSize: 11,
                }}
              />
            )}
          </Box>

          {/* 注册时间 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              gap: 1,
              mb: 2,
              opacity: 0.85,
            }}
          >
            <DateRangeIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2">
              注册于 {formatDate(user.created_at, 'YYYY-MM-DD')}
            </Typography>
          </Box>

          {/* 积分和成长值 */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: isMobile ? 'center' : 'flex-start',
              mb: 2,
            }}
          >
            {/* 积分 */}
            <Chip
              icon={<StarIcon sx={{ fontSize: 16 }} />}
              label={`${userPoints.balance || 0} 积分`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 215, 0, 0.3)',
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />

            {/* 等级（v3 LevelUp 2.0 - 月度成长等级） */}
            <Chip
              label={
                user.tier?.current_label
                  ? `${user.tier.current_label} 级别`
                  : `Lv.${growthInfo.level} ${growthInfo.name}`
              }
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />

            {/* 总观看剧集数 */}
            <Chip
              label={`📺 已观看 ${totalWatched} 部`}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 500,
              }}
            />
          </Box>

          {/* 月度成长值进度条（v3 LevelUp 2.0） */}
          {user.tier && !user.tier.is_max && (
            <Box sx={{ mt: 2, maxWidth: isMobile ? '100%' : 400 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  本月成长值: {user.tier.monthly_points || 0} / {user.tier.monthly_required || 100}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  下一等级: {(() => {
                    if (user.tier.next_level_label) return user.tier.next_level_label;
                    const code = user.tier.current_code;
                    if (typeof code === 'number' && code < 69) {
                      const sub = code % 10;
                      const tierNum = Math.floor(code / 10);
                      // X.9 → (X+1).1 跨段；其它 → +1
                      const nextCode = sub === 9 ? (tierNum + 1) * 10 + 1 : code + 1;
                      return `Lv${Math.floor(nextCode / 10)}.${nextCode % 10}`;
                    }
                    return '已封顶';
                  })()}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  ((user.tier.monthly_points || 0) / (user.tier.monthly_required || 100)) * 100,
                  100
                )}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ffd700',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}
          {/* 兼容兜底：未初始化 tier 的老用户走旧 growth */}
          {!user.tier && growthInfo.nextLevel && (
            <Box sx={{ mt: 2, maxWidth: isMobile ? '100%' : 400 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  成长值: {userPoints.growth_value || 0}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  下一等级: {growthInfo.nextLevel.name} ({growthInfo.nextLevel.min_value})
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(growthInfo.progress, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ffd700',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ProfileHeader;
